import React, { forwardRef, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  EButtonType,
  Input,
  KitIcons,
  ModalBackdrop,
  numberWithSpaces,
  Sidebar,
  SpinnerV2,
} from '@repo/apg-ui';
import { searchActions } from '../../store/search';
import { Basket, productsActions } from '../../store/products';
import { AppState } from '../../store';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Loading, UiActions } from '../../store/ui';
import * as _actionsBasket from '../../store/products/actions';
import './Basket.scss';
import { BasketRow } from './BasketRow/BasketRow';
import OrderQuoteModal from '../Modal/OrderQuoteModal';
import ConfirmationModal from '../Modal/ConfirmationModal';
import DiscountSelector from '../RadioSelect/input';
import { useLocation } from 'react-router-dom';
import ShareCalculationModal from '../Modal/ShareCalculationModal';
import classNames from 'classnames';
import TransitionWrapper from 'react-bootstrap/TransitionWrapper';

interface Props {
  actionsBasket: typeof _actionsBasket;
  basket: Basket;
  productsActions: typeof productsActions;
  possibleBookingDates: any;
  uiActions: typeof UiActions;
  loading: Loading;
  isSticky?: boolean;
  removeFromBasket: () => void;
}

const formatCHF = (value) => {
  const numberValue = Number(value.toString().split(' ').join('')) || 0;
  const formatter = new Intl.NumberFormat('de-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  let formattedValue = formatter.format(numberValue);

  // Ensure the thousands separator is a blank space
  formattedValue = formattedValue.replace(/’/g, ' ');

  return formattedValue;
};

const BasketComponent = ({
  actionsBasket,
  basket,
  productsActions,
  possibleBookingDates,
  uiActions,
  loading,
  removeFromBasket,
  isSticky,
}: Props) => {
  const { stats } = basket.basketProducts || {
    stats: { totalcount: 0, mediasum: 0 },
  };
  const intl = useIntl();
  const productCount = stats?.totalcount || 0;
  const [isHovered, setIsHovered] = React.useState(false);
  const [orderQuoteShow, setOrderQuoteShow] = React.useState(false);
  const cartModalRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    function handleClickOutside(event) {
      if (!basket.isBasketOpen) return;
      const searchParams = new URLSearchParams(location.search);
      const modalContent = searchParams.get('modalContent');
      if (modalContent) return;
      if (cartModalRef.current && !cartModalRef.current.contains(event.target)) {
        if (!orderQuoteShow) {
          actionsBasket.closeBasket();
        }
      }
    }

    //get basket content
    if (basket && basket?.id && basket?.reference && !basket?.basketProducts?.definition) {
      actionsBasket.getBasketContent({
        basket: basket.id,
        basketreference: basket.reference,
      });
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [shareCalculationShow, setShareCalculationShow] = React.useState(false);
  const [showConfirmationClear, setShowConfirmationClear] = React.useState(false);

  const clearBasket = () => {
    const payload = {
      entries: 'all',
      basketreference: basket.reference,
      basket: basket.id,
    };
    try {
      const response = actionsBasket.removeFromBasket(payload);
      if (response) {
        actionsBasket.clearBasket();
      } else {
        console.error('Failed to remove item from basket:', response);
      }
      setShowConfirmationClear(false);
    } catch (err) {
      console.error('Error removing item from basket:', err);
    }
  };

  return (
    <>
      <div
        className={classNames('basket-summary', { isSticky: isSticky })}
        onClick={() => {
          if (loading?.basket !== true) {
            actionsBasket.openBasket();
          }
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {loading?.basket === true && (
          <div className='loader-backdrop'>
            <div className='basket-loader'>
              <SpinnerV2 relative />
            </div>
          </div>
        )}
        <div className='basket-icon-wrapper'>
          <div className='basket-icon'>{isHovered ? <KitIcons.WhiteCartIcon /> : <KitIcons.CartIcon />}</div>
          <div className='item-count-badge'>{productCount}</div>
        </div>
        <div className='basket-total'>
          <div className='basket-total-amount'>
            {productCount > 0
              ? stats?.mediasum
                ? formatCHF(stats.mediasum)
                : 0
              : intl.formatMessage({
                  id: 'ec.basket.emptyBasket',
                  defaultMessage: 'Leerer Warenkorb',
                })}
          </div>
          <div className='basket-total-broadcasts'>
            {productCount == 0 ? (
              ''
            ) : (
              <>
                <FormattedMessage id='ec.general.broadcasts' defaultMessage='Ausstrahlungen' /> :{' '}
                <span>{numberWithSpaces(stats.broadcasts)}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <BasketModal
        basket={basket}
        actionsBasket={actionsBasket}
        productsActions={productsActions}
        possibleBookingDates={possibleBookingDates}
        uiActions={uiActions}
        loading={loading}
        removeFromBasket={removeFromBasket}
        setOrderQuoteShow={setOrderQuoteShow}
        setShowConfirmationClear={setShowConfirmationClear}
        ref={cartModalRef}
        setShareCalculationShow={setShareCalculationShow}
        shareCalculationShow={shareCalculationShow}
        orderQuoteShow={orderQuoteShow}
      />
      <OrderQuoteModal show={orderQuoteShow} setHide={() => setOrderQuoteShow(false)} />
      <ShareCalculationModal
        show={shareCalculationShow}
        setHide={() => setShareCalculationShow(false)}
        basket={basket}
        uiActions={uiActions}
        loading={loading}
      />
      <ConfirmationModal
        show={showConfirmationClear}
        setHide={() => setShowConfirmationClear(false)}
        title={intl.formatMessage({
          id: 'input.placeholder.confirmClearingCartTitle',
          defaultMessage: 'Bestätigen Sie die Löschung Ihres Warenkorbs',
        })}
        message={intl.formatMessage({
          id: 'input.placeholder.confirmClearingCartMessage',
          defaultMessage: 'Sind Sie sicher, dass Sie alle Produkte in Ihrem Warenkorb löschen möchten?',
        })}
        confirmationButton={intl.formatMessage({
          id: 'input.placeholder.confirmClearingCartButton',
          defaultMessage: 'Alles löschen',
        })}
        doAction={clearBasket}
      />
    </>
  );
};

interface Props {
  basket: Basket;
  actionsBasket: typeof _actionsBasket;
  productsActions: typeof productsActions;
  possibleBookingDates: any;
  uiActions: typeof UiActions;
  loading: Loading;
  setOrderQuoteShow: (show: boolean) => void;
  setShareCalculationShow: (show: boolean) => void;
  setShowConfirmationClear: (show: boolean) => void;
  removeFromBasket: () => void;
  shareCalculationShow: boolean;
  orderQuoteShow: boolean;
}

const BasketModal = forwardRef<HTMLDivElement, Props>(
  (
    {
      basket,
      actionsBasket,
      productsActions,
      possibleBookingDates,
      uiActions,
      loading,
      setOrderQuoteShow,
      setShareCalculationShow,
      setShowConfirmationClear,
      removeFromBasket,
      shareCalculationShow,
      orderQuoteShow,
    }: Props,
    ref,
  ) => {
    const { basketProducts, isBasketOpen } = basket;
    const { products, stats } = basket.basketProducts || {
      products: [],
      stats: { totalcount: 0 },
    };
    const productsCount = stats?.totalcount || 0;
    const hasProducts = productsCount > 0;
    const definition = basketProducts?.definition;
    const possibleDiscounts = definition?.possiblediscounts || [];
    const discountCode = definition?.discountcode || '';

    const updateDiscount = (discountCode = null, online = null, ac = null) => {
      const payload: any = {
        basket: basket.id,
        basketreference: basket.reference,
        online: online,
        commission: ac,
      };
      if (discountCode) {
        payload.discountcode = discountCode;
      }
      actionsBasket.updateBasketDefinition(payload);
    };

    const parseMediasum = (mediasum: string | number): number => {
      if (typeof mediasum === 'string') {
        return parseFloat(mediasum.replace(/\s/g, '')) || 0;
      } else if (typeof mediasum === 'number') {
        return mediasum;
      }
      return 0;
    };

    const mediasum: number = parseMediasum(stats?.mediasum);
    const discount: number = stats?.discount || 0;
    const totalNetto: number = mediasum - discount;
    const commission: number = stats?.commission || 0;
    const totalNettoNetto: number = totalNetto - commission;

    return (
      <TransitionWrapper in={isBasketOpen} timeout={50}>
        {(state) =>
          isBasketOpen && (
            <ModalBackdrop duration={250} state={state}>
              <Sidebar duration={250} state={state}>
                {isBasketOpen && (
                  <div className={'basket-modal d-flex flex-column'} ref={ref}>
                    <div className={'basket-modal__header d-flex flex-column w-100 '}>
                      <div className='w-100 d-flex flex-row justify-content-between'>
                        <div className={'d-flex flex-column'}>
                          <b className={'font-34'}>
                            <FormattedMessage
                              id='ec.checkout.calculationSummary'
                              defaultMessage='Zusammenfassung Kalkulation'
                            />{' '}
                          </b>
                        </div>
                        <div className={'d-flex flex-row align-items-center'}>
                          <KitIcons.CloseIcon
                            onClick={() => actionsBasket.closeBasket()}
                            className={'basket-modal basket-modal--close cursor-pointer'}
                          />
                        </div>
                      </div>
                      <div className='basket-modal__subheader'>
                        <FormattedMessage id='ec.checkout.added' defaultMessage='Hinzugefügt' /> (
                        <span>{productsCount})</span>{' '}
                        <FormattedMessage id='ec.checkout.networks' defaultMessage='Netze' />
                        &nbsp;&nbsp;&nbsp;&nbsp;
                        <FormattedMessage id='ec.basket.totalBroadcasts' defaultMessage='Total Ausstrahlungen' />:{' '}
                        {productsCount == 0 ? 0 : numberWithSpaces(stats.broadcasts)}
                        &nbsp;&nbsp;&nbsp;&nbsp;
                        {productsCount > 0 && (
                          <Button
                            className='mt-0 white-space-nowrap'
                            buttonType={EButtonType.BasicLink}
                            onClick={() => setShowConfirmationClear(true)}
                          >
                            (
                            <FormattedMessage
                              id='input.placeholder.confirmClearingCartButton'
                              defaultMessage='Alles löschen'
                            />
                            )
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className={'basket-modal__body'}>
                      {products?.map((product, idx) => (
                        <BasketRow
                          key={`productRow${product.producturn}-${idx}`}
                          product={product}
                          basket={basket}
                          basketEntries={basketProducts}
                          actionsBasket={actionsBasket}
                          removeFromBasket={removeFromBasket}
                          closeBasket={() => actionsBasket.closeBasket()}
                        />
                      ))}
                    </div>
                    <div className='basket-modal__footer d-flex justify-content-between align-items-center'>
                      <button className='share-calculation' onClick={() => setShareCalculationShow(true)}>
                        <span className='share-calculation-body'>
                          <FormattedMessage id='ec.basket.shareCalculation' defaultMessage='Kalkulation downloaden' />
                        </span>
                        &nbsp;
                        <KitIcons.ShareIcon />
                      </button>
                      <div className='d-flex flex-column'>
                        <div>
                          <DiscountSelector
                            discountOptions={possibleDiscounts}
                            discountCode={discountCode}
                            updateDiscount={updateDiscount}
                          />
                        </div>
                        <div className='d-flex flex-row pt-2 align-items-center gap-3 fs-4'>
                          <div className='d-flex flex-row align-items-center me-4'>
                            <Input
                              name={'online-rebate'}
                              type='switch'
                              inputWrapperClass={'me-2'}
                              onChange={(_, value) => {
                                updateDiscount(null, value, null);
                              }}
                              checked={definition?.online > 0}
                            />
                            <div className='pt-2'>
                              <FormattedMessage id='ec.components.switchTextDiscount' defaultMessage='Online-Rabatt' />
                            </div>
                          </div>
                          <div className='d-flex flex-row align-items-center justify-content-center fs-4'>
                            <Input
                              name={'availabilityProducts'}
                              type='switch'
                              inputWrapperClass={'me-2'}
                              onChange={(_, value) => {
                                updateDiscount(null, null, value);
                              }}
                              checked={definition?.commission > 0}
                            />
                            <div className='pt-2'>
                              <FormattedMessage id='ec.components.switchTextAC' defaultMessage='BK' />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='basket-total-amount'>
                        {loading?.basket === true && (
                          <div className='loader-backdrop'>
                            <div className='basket-loader'>
                              <SpinnerV2 relative />
                            </div>
                          </div>
                        )}
                        <div className='total-row'>
                          <span className='total-label'>
                            <FormattedMessage id='ec.basket.totalBrutto' defaultMessage='Total brutto' />:
                          </span>
                          <span className='total-value'>{formatCHF(mediasum)}</span>
                        </div>
                        {stats?.discount > 0 && (
                          <div className='total-row'>
                            <span className='total-label'>{stats?.discountlabel}:</span>
                            <span className='total-value'>- {formatCHF(discount)}</span>
                          </div>
                        )}
                        {stats?.discount > 0 && stats?.commission > 0 && (
                          <div className='total-row'>
                            <span className='total-label'>
                              <FormattedMessage id='ec.basket.totalNetto' defaultMessage='Total netto/netto' />:
                            </span>
                            <span className='total-value'>{formatCHF(totalNetto)}</span>
                          </div>
                        )}
                        {stats?.commission > 0 && (
                          <div className='total-row'>
                            <span className='total-label'>{stats?.commissionlabel}:</span>
                            <span className='total-value'>- {formatCHF(commission)}</span>
                          </div>
                        )}
                        <div className='total-row'>
                          <span className='total-label'>
                            <FormattedMessage id='ec.basket.totalNetto' defaultMessage='Total netto/netto' />:
                          </span>
                          <span className='total-value'>{formatCHF(totalNettoNetto)}</span>
                        </div>
                      </div>

                      <div className='checkout-button'>
                        <Button
                          className='mt-0 ms-3 checkout-button invisible'
                          buttonType={EButtonType.PillMedium}
                          isDisabled={!hasProducts}
                          onClick={() => {
                            // setOrderQuoteShow(true)
                          }}
                        >
                          <KitIcons.CheckIcon />
                          <FormattedMessage id='ec.basket.check' defaultMessage='Durch APG|SGA prüfen lassen' />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </Sidebar>
            </ModalBackdrop>
          )
        }
      </TransitionWrapper>
    );
  },
);

function mapStateToProps(state: AppState, ownProps: any) {
  return {
    ...ownProps,
    ...state.products,
    ...state.locations,
    ...state.ui,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    productsActions: bindActionCreators(productsActions as any, dispatch),
    actionsSearch: bindActionCreators(searchActions as any, dispatch),
    uiActions: bindActionCreators(UiActions as any, dispatch),
    dispatch: dispatch,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(BasketComponent);
